import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { services } from '../data/mockData';
import WhatsAppFloatingButton from "../pages/WhatsAppFloatingButton";

const ServicesPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const location = useLocation();

useEffect(() => {
 if (location.hash) {
console.log(location.hash)
 const element = document.querySelector(location.hash);

 if (element) {
 const yOffset = -120; // 👈 stop before (change this value)
 const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

 window.scrollTo({ top: y, behavior: "smooth" });
 }
 }
}, [location.hash]);


  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 12 },
    },
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 lg:py-32 bg-navy text-white relative overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1600&q=80" 
            alt="Team collaboration"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-navy/80" />
        </div>
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="max-w-3xl"
          >
            <motion.span variants={itemVariants} className="service-tag bg-light-blue text-royal-blue">
              What We Offer
            </motion.span>
            <motion.h1 variants={itemVariants} className="text-5xl md:text-6xl font-bold mt-6 mb-8">
              Organic Marketing for <span className="text-blue-300">Investment Firms</span>
            </motion.h1>
            <motion.p variants={itemVariants} className="text-xl text-gray-300 leading-relaxed">
              A full suite of organic marketing services designed specifically for financial institutions—helping you build trust, visibility and deal flow over time.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid with People Images */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
            className="space-y-16"
          >
            {services.map((service, index) => {
              const isEven = index % 2 === 0;
              
              return (
                <motion.div
                  key={service.id}
                   id={service.id}
                  variants={itemVariants}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                >
                  
                  {/* Image */}
                  <div className={`relative overflow-hidden rounded-lg ${!isEven ? 'lg:order-2' : ''}`}>
                    <img
                      src={`/assets/${service.image}`}
                      alt={service.title}
                      className="w-full h-80 lg:h-96 object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      {/* <span className="service-tag bg-white/90 text-navy shadow-sm">
                        {service.id.toUpperCase()}
                      </span> */}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className={`space-y-4 ${!isEven ? 'lg:order-1' : ''}`}>
                    <h2 className="text-3xl font-bold text-navy">{service.title}</h2>
                    <p className="text-lg text-steel-blue leading-relaxed">{service.description}</p>
                    <Link 
                      to="/contact" 
                      className="inline-flex items-center text-royal-blue font-semibold group"
                    >
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 lg:py-32 bg-snow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants} className="text-center mb-16">
              <span className="service-tag bg-light-blue text-royal-blue">Our Process</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6 text-navy">How We Work</h2>
            </motion.div>

            <motion.div variants={containerVariants} className="grid md:grid-cols-4 gap-8">
              {[
                { step: '01', title: 'Discovery', description: 'Understanding your goals, audience, and market position.' },
                { step: '02', title: 'Strategy', description: 'Crafting campaign-driven strategies aligned with market themes.' },
                { step: '03', title: 'Execution', description: 'Producing sharp content and building ecosystem connections.' },
                { step: '04', title: 'Amplify', description: 'Distributing and measuring impact across channels.' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-royal-blue mx-auto mb-6 flex items-center justify-center">
                    <span className="text-xl font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-navy">{item.title}</h3>
                  <p className="text-steel-blue">{item.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
<WhatsAppFloatingButton />
      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-royal-blue relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1600&q=80" 
            alt="City skyline"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={containerVariants}
            className="text-center"
          >
            <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Brand?
            </motion.h2>
            <motion.p variants={itemVariants} className="text-blue-100 text-lg max-w-2xl mx-auto mb-8">
              Let's discuss how we can help elevate your brand across Asia's financial ecosystem.
            </motion.p>
            <motion.div variants={itemVariants}>
              <Link to="/contact" className="cta-button !bg-white !text-royal-blue hover:!bg-gray-100">
                Let's Build Influence
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
